/* eslint-disable react-hooks/refs -- intentional render-time ref read +
 * write. The ref tracks the previously-returned Map identity so this
 * hook can short-circuit "outer Map churned but every entry is
 * pointer-equal" cases — the usual symptom of `new Map()` being
 * allocated by a merge helper that does per-entry caching but doesn't
 * cache the outer container. Pure with respect to render output. */
import { useRef } from "react";

/**
 * Outer-Map stability layer for hooks that already return per-entry
 * stable Maps. Mirrors the pattern used by `useWorkspaceMentionsData`'s
 * `prevWorkspaceMembersRef` / `prevWorkspaceChannelsRef` guards, but for
 * Map outputs.
 *
 * If every entry in `next` is `===` to the entry at the same key in the
 * previously-returned Map (and the size matches), returns the previous
 * Map reference. Otherwise returns `next` and remembers it for the next
 * call.
 *
 * Why this is needed:
 *
 * `mergeRenderUserMap` / `mergeActiveUserMap` reuse per-entry references
 * via their entry cache, so individual `RenderUser` / `ActiveUser`
 * values stay reference-stable when their content didn't change. But
 * the outer container is `new Map()` on every call, so consumers that
 * destructure the hook tuple — `const [activeUserMap] = useActiveUserMap()` —
 * see a fresh reference on every Zero IVM tick that touches user_presence,
 * even when zero entries actually changed. The fresh tuple causes a React
 * update, which cascades through `MessageArea` and its descendants for
 * nothing.
 *
 * Wrapping the merged Map in `useStableMap` closes that loop:
 *
 *   const merged = useMemo(() => mergeActiveUserMap(...), [...]);
 *   return [useStableMap(merged), status];
 *
 * Now consumers see a stable Map identity unless an entry actually
 * changed.
 */
export function useStableMap<K, V>(next: Map<K, V>): Map<K, V> {
  const prevRef = useRef<Map<K, V> | null>(null);
  const prev = prevRef.current;
  if (prev && prev.size === next.size) {
    let allEqual = true;
    for (const [k, v] of next) {
      if (prev.get(k) !== v) {
        allEqual = false;
        break;
      }
    }
    if (allEqual) return prev;
  }
  prevRef.current = next;
  return next;
}
